<script setup lang="ts">
import { onMounted, onUnmounted, watch } from "vue";
import IconAtom from "@/common/atoms/IconAtom.vue";

interface Props {
  title?: string; // Kept for compatibility, but mainly using message
  message: string;
  show: boolean;
  variant?: "success" | "warning" | "error";
  actionLabel?: string;
  actionStyle?: "text" | "button";
  placement?: "top" | "bottom";
  /** Wrap onto multiple lines instead of ellipsising. Needed for server error details, which run well past one line. */
  wrap?: boolean;
  /** Milliseconds before auto-close. Raise it for messages the user has to read and act on. */
  autoCloseMs?: number;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  variant: "success",
  actionLabel: undefined,
  actionStyle: "text",
  placement: "top",
  wrap: false,
  autoCloseMs: 3000,
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "action"): void;
}>();

const closeToast = () => {
  emit("close");
};

const handleAction = () => {
  emit("action");
};

let autoCloseTimer: ReturnType<typeof setTimeout> | undefined;

const clearAutoClose = () => {
  clearTimeout(autoCloseTimer);
  autoCloseTimer = undefined;
};

const scheduleAutoClose = () => {
  clearAutoClose();
  autoCloseTimer = setTimeout(closeToast, props.autoCloseMs);
};

watch(
  [() => props.show, () => props.message, () => props.autoCloseMs],
  ([show]) => {
    if (show) {
      scheduleAutoClose();
    } else {
      clearAutoClose();
    }
  },
);

onMounted(() => {
  if (props.show) scheduleAutoClose();
});

onUnmounted(clearAutoClose);
</script>

<template>
  <Teleport to="body">
    <Transition name="toast-fade">
      <div
        v-if="show"
        class="success-toast"
        :class="`success-toast--${placement}`"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <!-- Leading Content: Icon -->
        <div class="toast-icon-container">
          <div class="icon-bg" :class="`icon-bg--${variant}`">
            <IconAtom
              :name="
                variant === 'success'
                  ? 'checkmark'
                  : variant === 'warning'
                    ? 'alert-circle'
                    : 'close'
              "
              :size="14"
              color="#FFFFFF"
              :stroke-width="3"
            />
          </div>
        </div>

        <!-- Content: Message -->
        <div class="toast-content">
          <p class="toast-message" :class="{ 'toast-message--wrap': wrap }">
            {{ message }}
          </p>
        </div>

        <!-- Trailing Content: Action & Close Button -->
        <div class="toast-actions">
          <button
            v-if="actionLabel"
            type="button"
            class="action-button"
            :class="{ 'action-button--pill': actionStyle === 'button' }"
            @click="handleAction"
          >
            {{ actionLabel }}
          </button>
          <button
            type="button"
            class="close-button"
            aria-label="Dismiss notification"
            @click="closeToast"
          >
            <IconAtom name="close" :size="20" color="#FFFFFF" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/design-tokens" as *;

.success-toast {
  position: fixed;
  top: $spacing-6;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000; /* Ensure it's on top of everything */

  display: flex;
  align-items: center;
  max-width: min(
    #{$size-modal-sm - $spacing-5 * 3},
    calc(100vw - #{$spacing-3} * 2)
  );
  min-width: 300px;
  background-color: #22282f; /* surface/inverse */
  border-radius: 12px;
  box-shadow: 0px 25px 50px -12px rgba(0, 13, 51, 0.25);
  padding: 0 12px 0 16px; /* pl-16px pr-12px */
  overflow: hidden;
}

.success-toast--bottom {
  top: auto;
  bottom: max(#{$spacing-6}, env(safe-area-inset-bottom));
}

.toast-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-4 $spacing-2; /* py-4 px-2 */
  flex-shrink: 0;
}

.icon-bg {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &--success {
    background-color: #50b428; /* content/positive */
  }

  &--warning {
    background-color: #f59e0b; /* content/warning (Yellow/Orange) */
  }

  &--error {
    background-color: #e52931; /* content/negative */
  }
}

.toast-content {
  display: flex;
  align-items: center;
  padding: $spacing-4 $spacing-2; /* py-4 px-2 */
  flex-grow: 1;
  // Without this a flex item refuses to shrink below its content, so the message below can neither
  // wrap nor ellipsise — it just overflows and gets clipped by the container.
  min-width: 0;
}

.toast-message {
  font-family: "Satoshi", sans-serif;
  font-weight: 500; /* Medium */
  font-size: $txt-small-size;
  line-height: $txt-small-line-height;
  letter-spacing: 0.3px;
  color: #ffffff; /* content/inverse */
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--wrap {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    overflow-wrap: anywhere;
  }
}

.toast-actions {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  height: 100%;
}

.action-button {
  background: transparent;
  border: none;
  padding: $spacing-1-5 $spacing-2;
  border-radius: 12px;
  color: #ffffff;
  font-family: "Satoshi", sans-serif;
  font-weight: 700; /* Bold */
  font-size: $txt-xsmall-plus-size;
  line-height: $txt-xsmall-line-height;
  letter-spacing: 0.3px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
}

.action-button--pill {
  @include txt-small-plus;
  background-color: $color-surface-default;
  color: $color-content-default;
  padding: $spacing-1 $spacing-3;
  border-radius: $radius-full;

  &:hover {
    background-color: $color-neutral-70;
  }
}

.close-button {
  background: none;
  border: none;
  padding: $spacing-1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
}

/* Transition */
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -20px);
}
</style>
